<template>
    <div>
        <div class="subdomain-home">
            <common-content-spacer :height="getContentHeight">
                <img v-if="companyCover" class="subdomain-home--cover" :src="companyCover" :alt="$t('alt.company_avatar')" />
                <div :class="descriptionClass">
                    <div v-if="company?.descriptionTextContent?.originalText" class="subdomain-home--description__text">
                        <common-content-spacer height="huge">
                            <common-page-header
                                :size="getHeaderSize"
                                :heading="$t('common.microcopy.about_us')"
                                :isCenter="false"
                            ></common-page-header>
                            <div class="subdomain-home--description__text--description">
                                {{ getTranslation(company?.descriptionTextContent) }}
                            </div>
                        </common-content-spacer>
                    </div>
                    <div v-if="companyPhotos?.length" class="subdomain-home--description__slider">
                        <carousel
                            :autoplay="companyPhotos?.length > 1 ? 2000 : 0"
                            :itemsToScroll="1"
                            :itemsToShow="1"
                            :wrapAround="true"
                            :transition="2000"
                        >
                            <slide v-for="slide in companyPhotos" :key="slide">
                                <img class="carousel__item" :src="slide?.url" :alt="$t('alt.company_cover')" />
                            </slide>
                        </carousel>
                    </div>
                </div>

                <div v-if="companyOffers?.length">
                    <div class="subdomain-home--offers">
                        <common-content-spacer height="huge">
                            <common-page-header
                                :size="getHeaderSize"
                                :heading="$t('pages.home.home.subdomain.popular_offer')"
                                :isCenter="true"
                            ></common-page-header>
                            <common-grid-items class="subdomain-home__grid">
                                <offer-card-component
                                    v-for="offer in companyOffersFiltered"
                                    :key="offer.id"
                                    :offer="offer"
                                    @location-click="locationClicked"
                                ></offer-card-component>
                            </common-grid-items>
                        </common-content-spacer>
                    </div>
                    <div v-if="companyOffersFiltered?.length > 6">
                        <common-buttons align="center" class="subdomain-home--offers__more">
                            <common-button
                                :label="$t('pages.home.home.subdomain.show_more')"
                                :isSubmittable="false"
                                type="primary"
                                @click="$router.push({ name: RoutesEnum.SUBDOMAIN.OFFER })"
                            ></common-button>
                        </common-buttons>
                    </div>
                </div>
                <div v-if="companyUsers.length" class="subdomain-home--team">
                    <common-page-header
                        :size="getHeaderSize"
                        :heading="$t('common.microcopy.our_team')"
                        :isCenter="false"
                    ></common-page-header>

                    <carousel
                        v-if="!isTablet"
                        ref="usersSlider"
                        class="subdomain-home--team--users"
                        :breakpoints="null"
                        :autoplay="companyUsers.length > 3 ? 2000 : 0"
                        :itemsToScroll="1"
                        :itemsToShow="usersCount"
                        :wrapAround="true"
                        :transition="2000"
                        :snapAlign="'center-even'"
                    >
                        <slide v-for="user in companyUsers" :key="user.id" class="subdomain-home--team--users__slide">
                            <common-user-avatar
                                class="subdomain-home--team--users__avatar"
                                :user="user"
                                :shouldLeadToProfile="user?.showPublicProfile"
                                :showOrginalImage="true"
                                :size="getHeaderSize"
                                @avatar-click="onAvatarClick"
                            ></common-user-avatar>
                            <div class="subdomain-home--team--users__name">
                                <p>{{ user.firstName }}</p>
                                <p>{{ user.lastName }}</p>
                            </div>
                        </slide>
                    </carousel>
                    <common-grid-items v-else class="users-grid">
                        <div v-for="user in companyUsers" :key="user.id" class="users-grid__item">
                            <common-user-avatar
                                class="subdomain-home--team--users__avatar"
                                :user="user"
                                :shouldLeadToProfile="user?.showPublicProfile"
                                :showOrginalImage="true"
                                :size="getHeaderSize"
                                @avatar-click="onAvatarClick"
                            ></common-user-avatar>
                            <div class="subdomain-home--team--users__name">
                                <p>{{ user.firstName }}</p>
                                <p>{{ user.lastName }}</p>
                            </div>
                        </div>
                    </common-grid-items>
                </div>
                <div v-if="companyRatings.length" class="subdomain-home--rating">
                    <user-ratings
                        :numberOfRatings="companyAvgRating?.numberOfRatings"
                        :averageRating="companyAvgRating.averageRating ? companyAvgRating.averageRating : 0"
                        :ratings="companyRatings"
                    ></user-ratings>
                </div>
                <div v-if="locations?.length" class="subdomain-home--contact">
                    <common-page-header
                        :size="getHeaderSize"
                        :heading="$t('pages.contact.contact.contact')"
                        :isCenter="false"
                    ></common-page-header>
                    <contact-us-segment :showHeader="false" :markers="userMarkers" :locations="locations"></contact-us-segment>
                </div>
            </common-content-spacer>
        </div>
        <user-preview-modal
            v-if="isPreviewUserModalOpen"
            :userPreview="userPreview"
            @close="closeUserPreviewModal"
        ></user-preview-modal>
    </div>
</template>

<script>
    import 'vue3-carousel/dist/carousel.css'
    import CompanyAPI from 'api/CompanyAPI'
    import RatingAPI from 'api/RatingAPI'
    import OfferAPI from 'api/OfferAPI'
    import RoutesEnum from 'enums/routes.enum'
    import Store from '~/store'
    import OfferCardComponent from 'components/page-elements/offer/OfferCardComponent'
    import ContactUsSegment from 'components/page-elements/common/ContactUsSegment'
    import PhotoTypeEnum from 'enums/photo_type.enum'
    import { Carousel, Slide } from 'vue3-carousel'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import { setSiteTitle, setMetaTitle, setMetaDescription } from '~/helpers'
    import UserRatings from 'components/page-elements/rating/UserRatings'
    import TranslateMixin from 'mixins/TranslateMixin'
    import UserPreviewModal from 'components/modals/user/UserPreviewModal'

    export default {
        mixins: [WindowResizeMixin, TranslateMixin],
        components: { OfferCardComponent, ContactUsSegment, Carousel, Slide, UserRatings, UserPreviewModal },
        data() {
            return {
                companyId: null,
                company: null,
                companyAvgRating: null,
                companyRatings: [],
                companyOffers: null,
                companyUsers: [],
                RoutesEnum,
                PhotoTypeEnum: PhotoTypeEnum,
                companyPhotos: [],
                currentUserIndex: 0,
                displayUserCount: 4,
                rotationInterval: null,
                isPreviewUserModalOpen: false,
                userPreview: null,
            }
        },

        computed: {
            companyCover() {
                return Store.getters['data/getSubdomainCompany'].photoCover || ''
            },

            descriptionClass() {
                return {
                    'subdomain-home--description': true,
                    'subdomain-home--description--center': !this.company?.description?.length,
                }
            },

            getHeaderSize() {
                return 'large'
            },

            getContentHeight() {
                if (this.isSmartphoneLarge) {
                    return 'medium'
                }
                if (this.isTablet) {
                    return 'large'
                }

                return 'superior'
            },

            userMarkers() {
                return this.company?.locationsForCompany?.map((location) => ({
                    averageRating: this.companyAvgRating?.averageRating,
                    numberOfRatings: this.companyAvgRating?.numberOfRatings,
                    isCompanyOffer: true,
                    providerData: this.company,
                    coordinates: { lat: location.latitude, lng: location.longitude },
                    locationType: location.locationType,
                }))
            },

            usersCount() {
                if (this.$refs.usersSlider) {
                    this.$refs.usersSlider.updateSlideWidth()
                    this.$refs.usersSlider.updateBreakpointsConfigs()
                }

                if (this.isTablet) {
                    return 3
                }

                if (this.isSmartphoneLarge) {
                    return 2
                }

                return 4
            },

            companyOffersFiltered() {
                if (this.isTabletSmall) {
                    return this.companyOffers.slice(0, 6)
                }
                return this.companyOffers
            },

            locations() {
                return this.company?.locationsForCompany?.map((location) => ({
                    id: location.id,
                    address: location.address,
                    timeZone: location.timeZone,
                    location: location.location,
                    latitude: location.latitude,
                    longitude: location.longitude,
                    emails: location.emails,
                    telephones: location.telephones,
                    facebook: this.company.facebook,
                    instagram: this.company.instagram,
                    twitter: this.company.twitter,
                    linkedin: this.company.linkedIn,
                    tiktok: this.company.tiktok,
                    city: { name: location.city?.name },
                    companyServiceProvider: this.company,
                    locationType: location.locationType,
                }))
            },
        },

        methods: {
            fetchCompanyData() {
                if (!this.company) return

                this.companyPhotos = this.company.photos.filter((item) => item.type === PhotoTypeEnum.COMPANY_OTHER)
                setSiteTitle(`${this.company.name} | Bookeraj`)
                setMetaTitle(`${this.company.name} | Bookeraj`)
                if (this.company?.descriptionTextContent?.originalText) {
                    setMetaDescription(`${this.company?.descriptionTextContent?.originalText}`)
                }
                this.getOffers()
                this.companyAvgRating = {
                    id: this.company?.id,
                    numberOfRatings: this.company?.numberOfRatings,
                    averageRating: this.company?.averageRating,
                }
                RatingAPI.getRatingsForCompany({ companyId: this.company.id, take: 25, skip: 0 }).then(
                    (res) => (this.companyRatings = res),
                )
                CompanyAPI.getCompanyTeam(this.company.id).then((val) => {
                    this.companyUsers = val
                })
            },

            getOffers() {
                OfferAPI.getPublicCompanyOffersById(this.company.id).then((val) => {
                    this.companyOffers = val.slice(0, 10)
                })
            },

            locationClicked(location) {
                window.open(`https://www.google.com/maps/place/${location.lat},${location.lng}`, '_blank')
            },

            onAvatarClick(event) {
                if (!event.showPublicProfile) {
                    this.isPreviewUserModalOpen = true
                    this.userPreview = event
                }
            },

            closeUserPreviewModal() {
                this.isPreviewUserModalOpen = false
                this.userPreview = null
            },
        },

        mounted() {
            this.company = Store.getters['data/getSubdomainCompany']
            if (this.$route.query.finalize) {
                this.$toast.success(this.$t('pages.auth.login.messages.success'))
            }
            if (this.company) {
                this.fetchCompanyData()
            }
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .subdomain-home {
        padding-bottom: 50px;
        background-color: $color-white;
        &--cover {
            width: 100%;
            height: auto;
            border-radius: 15px;
        }

        &--description {
            @include breakpoint(tablet) {
                flex-wrap: wrap;
            }
            display: flex;
            justify-content: space-between;
            align-items: flex-start;

            &--center {
                justify-content: center;
            }

            &__text {
                @include breakpoint(tablet) {
                    max-width: 100%;
                    padding-right: 0;
                    padding-bottom: 80px;
                }
                max-width: 50%;
                padding-right: 60px;

                &--header {
                    font-size: $font-size--heading;
                    margin-bottom: 20px;
                    color: $subdomain-black1;
                    font-weight: bold;
                }

                &--description {
                    padding-right: 50px;
                    font-size: 16px;
                    color: $subdomain-medium2;
                    white-space: pre-line;
                    @include breakpoint(tablet) {
                        padding-right: 5px;
                        word-break: break-word;
                        hyphens: auto;
                        overflow-wrap: break-word;
                    }
                }
            }

            &__slider {
                @include breakpoint(tablet) {
                    width: 100%;
                }
                width: 50%;

                img {
                    border-radius: 20px !important;
                    max-height: 400px;
                    padding: 10px;
                }
            }
        }

        &--offers {
            position: relative;
            @include breakpoint(tablet) {
                padding: 30px;
            }

            @include breakpoint(tablet-small) {
                overflow: auto;
                max-height: unset;
            }

            @include breakpoint(smartphone-large) {
                padding: 10px;
            }
            background-color: $color--bg-dashboard;
            padding: 50px;
            border-top-left-radius: 20px;
            border-top-right-radius: 20px;
            max-height: 560px;
            overflow-y: hidden;
            &__header {
                font-size: $font-size--heading;
                color: $subdomain-black1;
                font-weight: bold;
            }

            &__more {
                @include breakpoint(tablet-small) {
                    position: relative;
                    bottom: unset;
                }

                @include breakpoint(smartphone-large) {
                    padding: 10px;
                }
                background-color: $color--bg-dashboard;
                border-bottom-left-radius: 20px;
                border-bottom-right-radius: 20px;
                padding-bottom: 40px;
            }
        }

        &--contact {
            margin-top: 65px !important;

            .page-header {
                margin-bottom: 20px;
            }
        }

        &--team {
            @include breakpoint(tablet) {
                flex-wrap: wrap;
            }
            padding: 0 0 20px 0;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            background-color: $color--txt-base-6;
            background-color: white;

            .page-header {
                max-width: 220px;
                min-width: 220px;
                @include breakpoint(laptop) {
                    max-width: 200px;
                    min-width: 200px;
                }
                @include breakpoint(tablet) {
                    width: 100%;
                    margin-bottom: 50px;
                }
            }

            &--users {
                @include breakpoint(laptop) {
                    width: 720px;
                }
                @include breakpoint(tablet) {
                    width: 100%;
                }
                width: 900px;
                display: flex;
                justify-content: flex-end;

                .carousel__slide {
                    flex-wrap: wrap;
                }

                &__tablet {
                    width: 100% !important;
                    flex-wrap: wrap;
                    justify-content: flex-start;
                    .subdomain-home--team--users__slide {
                        width: auto !important;
                        margin: 20px;
                    }
                }

                &__slide {
                    @include breakpoint(laptop) {
                        width: calc(720px / 4) !important;
                    }
                    width: calc(900px / 4) !important;
                }

                &--item {
                    margin-left: 50px;
                }

                &__name {
                    @include breakpoint(laptop) {
                        width: 120px !important;
                    }
                    margin-top: 20px;
                    color: $subdomain-medium2;
                    font-size: 20px;
                    line-height: 20px;
                    text-align: center;
                    width: 200px !important;
                }

                &__avatar {
                    cursor: pointer;
                    @include breakpoint(tablet) {
                        height: 80px !important;
                        width: 80px !important;
                        img {
                            height: 80px !important;
                            width: 80px !important;
                        }
                    }

                    height: 120px !important;
                    width: 120px !important;
                    img {
                        height: 120px !important;
                        width: 120px !important;
                    }
                }
            }
        }

        .contact-us-segment__title {
            background-color: $color-white;
            padding-bottom: 20px;
            margin-bottom: 0;
            color: #333 !important;
            font-size: 44px !important;
        }
    }

    .subdomain-home__grid {
        @include breakpoint(tablet-small) {
            overflow: auto;
            grid-row-gap: 10px !important;
        }
        grid-row-gap: 200px !important;
    }

    .users-grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important;
        grid-row-gap: 40px !important;
        &__item {
            text-align: center;

            .avatar {
                margin: 0 auto;
            }
        }

        .subdomain-home--team--users__name {
            width: 100% !important;
        }
    }

    .subdomain-layout--navy_black,
    .subdomain-layout--black {
        .subdomain-home {
            background-color: $subdomain-midnight;

            &--rating {
                background-color: $subdomain-black2;
                .user-ratings {
                    border: unset;
                }
                .single-rating__info--name {
                    color: $subdomain-blue;
                }

                .rating-stars__type-secondary {
                    color: $color-progress-bar--active;
                }
            }

            &--offers {
                background-color: $subdomain-black2;

                &__more {
                    background-color: $subdomain-black2;
                }
            }

            &--team {
                background-color: $subdomain-midnight;
            }

            &--description {
                &__text {
                    &--description {
                        color: $subdomain-light;
                    }
                }
            }

            .user-ratings__left-side--rating-top > u {
                color: $color-progress-bar--active;
            }

            .single-rating {
                background-color: $subdomain-midnight;

                &__comment {
                    color: $color-gray-4;
                }

                &__info--time {
                    color: $color-gray-3;
                }
            }

            .contact-us-segment__title {
                background-color: $subdomain-midnight;
                color: $subdomain-dark1 !important;
            }
        }
    }

    .subdomain-layout--navy_black,
    .subdomain-layout--black,
    .subdomain-layout--white {
        .subdomain-home {
            .user-ratings__ratings {
                margin: 0 20px;

                @include breakpoint(tablet) {
                    margin: 10px 20px;
                }
            }
        }
    }

    .subdomain-layout--white {
        .subdomain-home {
            &--rating {
                background-color: $color--bg-dashboard;
                .user-ratings {
                    border: unset;
                }
            }

            .single-rating {
                background-color: $color-white;
            }
        }
    }

    .subdomain-layout--navy_black {
        .subdomain-home {
            &--team {
                &--users {
                    &__name {
                        color: $subdomain-blue;
                    }
                }
            }
        }
    }

    .subdomain-layout--black {
        .subdomain-home {
            &--team {
                &--users {
                    &__name {
                        color: $subdomain-light;
                    }
                }
            }
        }
    }
</style>
